import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import equal from 'fast-deep-equal';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { MspCategoryDigitCodesEnum } from 'APIPackage/KnmModule/enums/MspCategoryDigitCodesEnum';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { getIsPlanErknmStatusConsidered } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { mspCodesByMspCategoryDigitCodes } from 'KnmPackage/MainModule/constants/mspCodesByMspCategoryDigitCodes';
import { useKnmSetFnsLookupLoadingFlags } from 'KnmPackage/MainModule/hooks/useKnmSetFnsLookupLoadingFlags';
import { useKnmSetLegalEntityDataAction } from 'KnmPackage/MainModule/hooks/useKnmSetLegalEntityDataAction';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataErknmApproveApproved,
  getKnmDataNote,
  getKnmFormRef,
  getKnmIsChangePlanReasonActive,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsNew,
  getTargetEntitySelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { parsePassport } from 'CorePackage/MainModule/common/parsePassport';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './ErknmResultIntemSubjectInfo.module.scss';
import { composeValidators } from '../../../CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { MAX_CHARACTERS_ERROR, REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { makeMaxCharactersValidator } from '../../../CorePackage/MainModule/validators/maxCharacters';
import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { RafpRequestStatusTypeEnum } from '../../../APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from '../../../UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { KnmSnilsField } from '../../../KnmPackage/MainModule/components/KnmSnilsField';
import { ICountry } from '../../../APIPackage/CatalogsModule/interfaces/ICountry';
import { fnsCheckMessageByStatuses } from '../../ErknmSubjectModule/constants/fnsCheckMessageByStatuses';
import { FnsCheckStatusesEnum } from '../../ErknmSubjectModule/enums/FnsCheckStatusesEnum';
import { getCatalogKnmResponsibleTypesSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmResponsibileTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmResponsibileTypeSelectOption';
import {
  isFnsAddressFnsError,
  isOrganizationNameFnsError,
  isWorkAddressFnsError,
  renderFnsCheckMessage,
  renderFnsErvkEqualsError,
  valuesSubjectResultStatuses,
} from '../../../CorePackage/MainModule/common/renderFnsMessage';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';

export enum KnmResultItemSubjectTypesEnum {
  FIZ = 'FIZ',
  UR = 'UL',
  IP = 'IP',
  IFIZ = 'IFIZ',
  IUR = 'IUL',
  FIUR = 'FIUL',
  OFFICIAL = 'OFFICIAL',
}

interface IErknmResultItemSubjectInfoProps {
  ervkId: string | undefined;
  firstOrganizationName: string | undefined;
  id: string;
  initialCheckSubjectResult: IKnmCheckSubjectResult | null;
  inn: string | undefined;
  innLabelBlockId: string;
  isAutocomplete: IKnmErknmFormSubject['isAutocomplete'];
  isFiz: boolean | undefined;
  isNewOrganizationTemplate?: boolean;
  isOtherOrganization: boolean | undefined;
  isWatchOnly: boolean;
  kpp: string | undefined;
  kppLabelBlockId: string;
  lookupData?: ILegalEntityLookup | boolean | undefined;
  name: string;
  ogrn: string | undefined;
  ogrnLabelBlockId: string;
  organizationNameBlockId: string;
  positionBlockId: string;
  rafpLabelBlockId: string;
  rafpNza: string | undefined;
  removeOtherOrganization?: () => void;
  setCheckByInnMessage?: Dispatch<SetStateAction<string | undefined>>;
  setLookupData?: Dispatch<SetStateAction<ILegalEntityLookup | boolean | undefined>>;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  snils: string | undefined;
  subjectResult: IKnmCheckSubjectResult | null;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
}

interface IErknmResultItemSubjectInfoStoreProps {
  catalogKnmResponsibleTypesSelectOptions: IKnmResponsibileTypeSelectOption[];
  formRef: IKnmFormRef | undefined;
  isChangePlanReasonActive: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isNewKnm: boolean;
  isSupervisor: boolean | undefined;
  knmDataApprovedType: ApprovedTypesEnum | null | undefined;
  knmDataNote: string | null | undefined;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
}

const ErknmResultItemSubjectInfoWithoutStore = ({
  ervkId,
  firstOrganizationName,
  id,
  initialCheckSubjectResult,
  inn,
  innLabelBlockId,
  isAutocomplete,
  isFiz,
  isNewOrganizationTemplate,
  isOtherOrganization,
  isWatchOnly,
  kpp,
  kppLabelBlockId,
  lookupData,
  name,
  ogrn,
  ogrnLabelBlockId,
  organizationNameBlockId,
  positionBlockId,
  rafpLabelBlockId,
  rafpNza,
  removeOtherOrganization,
  setCheckByInnMessage,
  setLookupData,
  smevRequestStatus,
  snils,
  subjectResult,
  subjectType,
  catalogKnmResponsibleTypesSelectOptions,
  formRef,
  isChangePlanReasonActive,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isNewKnm,
  isSupervisor,
  knmDataApprovedType,
  knmDataNote,
  knmPlanStatus,
  subjectTypeOptions,
}: IErknmResultItemSubjectInfoProps & IErknmResultItemSubjectInfoStoreProps) => {
  const isKnmPlanStatusConsidered: boolean = getIsPlanErknmStatusConsidered(knmPlanStatus);
  const push = formRef?.current.mutators.push;

  let subjectTypeCode = subjectType?.code;

  if (!subjectTypeCode) {
    subjectTypeCode = subjectTypeOptions?.find(i => i.title === subjectType?.title)?.code;
  }

  const isIFIZ = subjectTypeCode === KnmResultItemSubjectTypesEnum.IFIZ;
  const isUR = subjectTypeCode === KnmResultItemSubjectTypesEnum.UR;
  const isIP = subjectTypeCode === KnmResultItemSubjectTypesEnum.IP;
  const isOFFICIAL = subjectTypeCode === KnmResultItemSubjectTypesEnum.OFFICIAL;
  const isFIUR = subjectTypeCode === KnmResultItemSubjectTypesEnum.FIUR;
  const isIUR = subjectTypeCode === KnmResultItemSubjectTypesEnum.IUR;

  const { setFnsLookupByOgrnLoadingFlag, setFnsLookupByInnLoadingFlag } = useKnmSetFnsLookupLoadingFlags();
  const { dispatchKnmSetLegalEntityData } = useKnmSetLegalEntityDataAction();

  const fnsCheckMessage = fnsCheckMessageByStatuses.find(item =>
    equal(item.statuses.sort(), valuesSubjectResultStatuses(subjectResult)?.sort()),
  );

  const isFnsCheckInProgress = fnsCheckMessage?.type === FnsCheckStatusesEnum.IN_PROGRESS;
  const isFnsCheckFound = fnsCheckMessage?.type === FnsCheckStatusesEnum.FOUND;
  const isFizFieldName = `${name}.isFiz`;
  const ogrnFieldName = `${name}.ogrn`;
  const innFieldName = `${name}.inn`;
  const positionFieldName = `${name}.position`;
  const organizationNameFieldName = `${name}.organizationName`;
  const subjectTypeFieldName = `${name}.subjectType`;
  const kppFieldName = `${name}.kpp`;
  const rafpNzaFieldName = `${name}.rafpNza`;
  const isForeignSubjectAgencyNotRegistredInRfFieldName = `${name}.isForeignSubjectAgencyNotRegistredInRf`;
  const countryFieldName = `${name}.country`;
  const locationAddressFieldName = `${name}.locationAddress`;
  const workAddressFieldName = `${name}.workAddress`;
  const factualAddressFieldName = `${name}.factualAddress`;
  const snilsFieldName = `${name}.snils`;
  const documentFieldName = `${name}.document`;
  const [rafpNzaNotFoundText, setRafpNzaNotFoundText] = useState<string | undefined>(
    'Запрашиваемые сведения отсутствуют в РАФП, проверьте введенные значения. Если у данного иностранного\n' +
      'юридического лица нет филиалов и представительств, зарегистрированных на территории РФ, то нажмите\n' +
      'галочку под типом контролируемого лица',
  );

  const handleLegalEntityAutoCompleteSelect = useCallback(
    (legalEntityLookup: ILegalEntityLookup) => {
      if (subjectTypeCode !== KnmResultItemSubjectTypesEnum.IUR) {
        const {
          ogrn: legalEntityOgrn,
          inn: legalEntityInn,
          orgName: legalEntityOrgName,
          dateReg: legalEntityDateReg,
          status: legalEntityStatus,
          leAddress: legalEntityLeAddress,
          workAddress: legalEntityWorkAddress,
          mspCategory: legalMspCategory,
          mspRegistryDate: legalMspRegistryDate,
        } = legalEntityLookup;

        formRef?.current.change(`${name}.organizationAutocomplete` as keyof IKnmForm, true);
        if (legalEntityOgrn) {
          formRef?.current.change(ogrnFieldName as keyof IKnmForm, legalEntityOgrn);
        }
        if (legalEntityInn) {
          formRef?.current.change(innFieldName as keyof IKnmForm, legalEntityInn);
        }
        if (legalEntityInn && legalEntityOgrn) {
          formRef?.current.change(`${name}.isAutocomplete` as keyof IKnmForm, true);
        }
        if (legalEntityOrgName) {
          formRef?.current.change(organizationNameFieldName as keyof IKnmForm, legalEntityOrgName);
        }
        if (legalEntityWorkAddress && isUR) {
          formRef?.current.change(workAddressFieldName as keyof IKnmForm, legalEntityWorkAddress);
        }
        if (legalEntityLeAddress && isUR) {
          formRef?.current.change(locationAddressFieldName as keyof IKnmForm, legalEntityLeAddress);
        }
        if (legalEntityLeAddress && isIP) {
          formRef?.current.change(factualAddressFieldName as keyof IKnmForm, legalEntityLeAddress);
        }
        if (legalEntityDateReg) {
          formRef?.current.change(
            `${name}.registrationDate` as keyof IKnmForm,
            formatDate({ date: legalEntityDateReg, dateFormat: DATE_FORMAT_LONG_FNS }),
          );
        }
        if (legalEntityStatus) {
          formRef?.current.change(`${name}.organizationStatus` as keyof IKnmForm, legalEntityStatus);
        }
        if (legalMspCategory) {
          formRef?.current.change(
            `${name}.mspCode` as keyof IKnmForm,
            mspCodesByMspCategoryDigitCodes[Number(legalMspCategory) as MspCategoryDigitCodesEnum],
          );
        }
        if (legalMspRegistryDate) {
          formRef?.current.change(`${name}.mspRegistryDate` as keyof IKnmForm, legalMspRegistryDate);
        }
      }
    },
    [formRef, subjectType, innFieldName, name, ogrnFieldName, organizationNameFieldName, push, isUR, isIP],
  );

  useEffect(() => {
    if (inn && ogrn) {
      dispatchKnmSetLegalEntityData({
        inn,
        ogrn,
      });
    }
  }, [dispatchKnmSetLegalEntityData, inn, ogrn]);

  const handleAutoCompleteClear = useCallback(() => {
    formRef?.current.change(ogrnFieldName as keyof IKnmForm);
    formRef?.current.change(innFieldName as keyof IKnmForm);
    formRef?.current.change(organizationNameFieldName as keyof IKnmForm);
    formRef?.current.change(workAddressFieldName as keyof IKnmForm);
    formRef?.current.change(locationAddressFieldName as keyof IKnmForm);
    dispatchKnmSetLegalEntityData(undefined);
    formRef?.current.change(`${name}.isAutocomplete` as keyof IKnmForm, false);
    formRef?.current.change(`${name}.mspCode` as keyof IKnmForm);
  }, [dispatchKnmSetLegalEntityData, formRef, innFieldName, name, ogrnFieldName, organizationNameFieldName]);

  const handleRafpNzaChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      // Сбросить ИНН и КПП
      if (v.target.value) {
        formRef?.current.change(innFieldName as keyof IKnmForm, undefined);
        formRef?.current.change(kppFieldName as keyof IKnmForm, undefined);
        if (v.target.value !== rafpNza) {
          setRafpNzaNotFoundText(undefined);
        } else if (isRafpNzaNotFound) {
          setRafpNzaNotFoundText(
            'Запрашиваемые сведения отсутствуют в РАФП, проверьте введенные значения. Если у данного иностранного\n' +
              'юридического лица нет филиалов и представительств, зарегистрированных на территории РФ, то нажмите\n' +
              'галочку под типом контролируемого лица',
          );
        }
      }
    },
    [formRef, innFieldName, kppFieldName],
  );

  const handleKppChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      if (v.target.value) {
        // Сбросить РАФП
        formRef?.current.change(rafpNzaFieldName as keyof IKnmForm, undefined);
      }
    },
    [formRef, rafpNzaFieldName],
  );

  useEffect(() => {
    if ((typeof isFiz === 'boolean' || typeof isOFFICIAL === 'boolean') && !isAutocomplete) {
      if (isFiz || isOFFICIAL) {
        formRef?.current.change(ogrnFieldName as keyof IKnmForm);
        formRef?.current.change(`${name}.okveds` as keyof IKnmForm);
      }
    }
  }, [
    formRef,
    isAutocomplete,
    isFiz,
    isOFFICIAL,
    name,
    ogrnFieldName,
    organizationNameFieldName,
    subjectTypeFieldName,
  ]);

  const isFieldEnabled =
    (!isWatchOnly &&
      !isFnsCheckInProgress &&
      isSupervisor &&
      (isNewOrganizationTemplate || isNewKnm || isKnmStatusFillInProcess || isKnmStatusReadyForApprove) &&
      !(isKnmPlanStatusConsidered && knmDataApprovedType === ApprovedTypesEnum.APPROVED && !!knmDataNote)) ||
    isChangePlanReasonActive;

  const isRafpNzaSent = smevRequestStatus === RafpRequestStatusTypeEnum.REQUEST_SENT;
  const isRafpNzaNotFound = smevRequestStatus === RafpRequestStatusTypeEnum.NOT_FOUND;
  const isRafpNzaError = smevRequestStatus === RafpRequestStatusTypeEnum.ERROR_RETRY;
  const isInnFieldEnabled = isFieldEnabled || isRafpNzaNotFound;

  const isOrganizationNameFieldEnabledWithoutFiz =
    !isWatchOnly &&
    isSupervisor &&
    (isNewOrganizationTemplate ||
      isNewKnm ||
      isKnmStatusFillInProcess ||
      (isKnmStatusReadyForApprove && (isFiz || isOFFICIAL))) &&
    !(isKnmPlanStatusConsidered && knmDataApprovedType === ApprovedTypesEnum.APPROVED && !!knmDataNote);

  const isOrganizationNameFieldEnabled = (isFiz || isOFFICIAL) && isOrganizationNameFieldEnabledWithoutFiz;

  const getOrganizationNameFieldRequired = () => !(isOrganizationNameFieldEnabledWithoutFiz && getIsFizValue());

  const getIsFizValue = () =>
    formRef?.current.getFieldState(subjectTypeFieldName as keyof IKnmForm)?.value === KnmResultItemSubjectTypesEnum.FIZ;

  const handleSubjectTypeChange = useCallback(
    (v: IDictionarySelectOption<ITargetEntity>) => {
      formRef?.current.change(isFizFieldName as keyof IKnmForm, v?.code === KnmResultItemSubjectTypesEnum.FIZ);
      formRef?.current.change(innFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(ogrnFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(rafpNzaFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(kppFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(organizationNameFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(workAddressFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(locationAddressFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(factualAddressFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(snilsFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(documentFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(`${name}.isAutocomplete` as keyof IKnmForm, false);
      formRef?.current.change(isForeignSubjectAgencyNotRegistredInRfFieldName as keyof IKnmForm, false);
      formRef?.current.change(countryFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(`${name}.okveds` as keyof IKnmForm, undefined);
      formRef?.current.change(`${name}.mspCode` as keyof IKnmForm, undefined);
      setCheckByInnMessage && setCheckByInnMessage(undefined);
    },
    [
      formRef,
      name,
      isFizFieldName,
      innFieldName,
      ogrnFieldName,
      rafpNzaFieldName,
      kppFieldName,
      organizationNameFieldName,
      isForeignSubjectAgencyNotRegistredInRfFieldName,
      countryFieldName,
    ],
  );

  const normalizeFio = (value: string | undefined) => {
    if (!value) return value;

    return value.replace(/[^а-яёa-z-`' ]/gi, '');
  };

  const isRafpFilled = !!rafpNza && /^\d{11}$/.test(rafpNza);
  const waitFromRafpWarning =
    isRafpFilled && !isRafpNzaSent ? 'Сохраните КНМ и ожидайте загрузки информации из РАФП' : undefined;
  const innOrSnilsWarning =
    (isFiz || isOFFICIAL) && !inn && !snils ? 'Необходимо заполнить либо ИНН, либо СНИЛС' : undefined;
  const filterUlCountries = (values: ICountry[]) => values?.filter(item => item.ulEnabled);
  const filterFlCountries = (values: ICountry[]) => values?.filter(item => item.flEnabled);

  /**
   * Список доступных типов КЛ
   */
  const availableSubjectTypes = useMemo(() => {
    return catalogKnmResponsibleTypesSelectOptions.filter(i => i.enabledAfter4127);
  }, [catalogKnmResponsibleTypesSelectOptions]);

  return (
    <>
      <Row>
        <FieldBlock
          size="content"
          label={
            <div className={styles.SubjectTypeTitle}>
              {getTitleFieldWithRedStar({
                label: 'Тип контролируемого лица',
                field: `${subjectTypeFieldName}`,
                isKnm: true,
              })}
              {isOtherOrganization && removeOtherOrganization && (
                <Button
                  className="shared-btn-add"
                  text="Удалить иное контролируемое лицо"
                  type="button"
                  variant="link"
                  color="primary"
                  disabled={isWatchOnly}
                  onClick={removeOtherOrganization}
                />
              )}
            </div>
          }
        >
          <SelectInputField
            id={`${id}.subjectType`}
            name={subjectTypeFieldName}
            options={availableSubjectTypes}
            disabled={!isFieldEnabled}
            size="large"
            onChange={handleSubjectTypeChange}
          />
        </FieldBlock>
      </Row>

      {subjectType && (
        <>
          <Row>
            {(isUR || isIP) && (
              <>
                <FieldBlock
                  label="Основной государственный регистрационный номер (ОГРН)"
                  size="half"
                  color="black"
                  id={ogrnLabelBlockId}
                >
                  <KnmOgrnField
                    name={ogrnFieldName}
                    clearable={isFieldEnabled && isAutocomplete}
                    disabled={!isFieldEnabled || isAutocomplete}
                    ogrn={ogrn}
                    onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                    onClear={handleAutoCompleteClear}
                    onLoadingChange={setFnsLookupByOgrnLoadingFlag}
                  />
                </FieldBlock>
              </>
            )}
            {!isIUR && !isIFIZ && (
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Идентификационный номер налогоплательщика (ИНН)',
                  field: `${innFieldName}`,
                  isKnm: true,
                })}
                size="half"
                color="black"
                id={innLabelBlockId}
              >
                <KnmInnField
                  name={innFieldName}
                  clearable={isInnFieldEnabled && isAutocomplete}
                  disabled={!isInnFieldEnabled || isAutocomplete || !!rafpNza || isRafpNzaSent}
                  inn={inn}
                  isFnsCheckFound={isFnsCheckFound}
                  isFiz={isFiz || isOFFICIAL}
                  setLookupData={setLookupData}
                  isShowAutoCompleteField={!isIUR}
                  onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                  onClear={handleAutoCompleteClear}
                  onLoadingChange={setFnsLookupByInnLoadingFlag}
                  warning={
                    innOrSnilsWarning ||
                    waitFromRafpWarning ||
                    (!(isFiz || isOFFICIAL) &&
                      !smevRequestStatus &&
                      renderFnsCheckMessage(subjectResult, lookupData, isAutocomplete))
                  }
                  setCheckByInnMessage={!ervkId ? setCheckByInnMessage : undefined}
                  ervkId={ervkId}
                  subjectType={subjectType}
                  checkSubjectResult={initialCheckSubjectResult}
                  isShowAdditionalMessages={true}
                />
                {isRafpNzaSent && <Hint text="Ожидайте загрузки информации из РАФП" />}
              </FieldBlock>
            )}
            {isFIUR && (
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Номер записи об аккредитации в РАФП',
                  field: `${rafpNzaFieldName}`,
                  isKnm: true,
                })}
                size="half"
                id={rafpLabelBlockId}
                color="black"
              >
                <TextInputField
                  name={rafpNzaFieldName}
                  disabled={!!inn || !!kpp || (!isRafpNzaNotFound && isRafpNzaSent)}
                  onChange={handleRafpNzaChange}
                  warning={waitFromRafpWarning}
                />
                {isRafpNzaSent && <Hint text="Ожидайте загрузки информации из РАФП" />}
                {isRafpNzaNotFound && <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>}
                {isRafpNzaError && (
                  <Hint
                    text="Запрашиваемые сведения не могут быть предоставлены в настоящий момент. Автоматический запрос будет
                    произведен через час"
                  />
                )}
              </FieldBlock>
            )}
            {(isFiz || isOFFICIAL) && (
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Страховой номер индивидуального лицевого счета (СНИЛС)',
                  field: `${snilsFieldName}`,
                  isKnm: true,
                })}
                size="half"
                color="black"
                id={`${id}.snils`}
              >
                <KnmSnilsField name={snilsFieldName} warning={innOrSnilsWarning} disabled={!isFieldEnabled} />
              </FieldBlock>
            )}
          </Row>
          {isFIUR && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Код причины постановки на учет (КПП)',
                  field: `${kppFieldName}`,
                  isKnm: true,
                })}
                size="half"
                id={kppLabelBlockId}
                color="black"
              >
                <TextInputField
                  name={kppFieldName}
                  disabled={isWatchOnly || !!rafpNza || isRafpNzaSent}
                  onChange={handleKppChange}
                  warning={waitFromRafpWarning}
                />
                {isRafpNzaSent && <Hint text="Ожидайте загрузки информации из РАФП" />}
              </FieldBlock>
            </Row>
          )}
          {(isUR || isIP || isFIUR) && (
            <Row>
              <FieldBlock label="Наименование проверяемого лица" id={organizationNameBlockId} color="black">
                <TextareaField
                  inputClassName={styles.OrganizationNameInput}
                  name={organizationNameFieldName}
                  resize="none"
                  clearable={isOrganizationNameFieldEnabled && isAutocomplete}
                  disabled={!isOrganizationNameFieldEnabled}
                  onClear={handleAutoCompleteClear}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                />
                {!smevRequestStatus &&
                  isOrganizationNameFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {(isIFIZ || isFiz || isOFFICIAL) && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Фамилия, имя, отчество (при наличии)',
                  field: `${organizationNameFieldName}`,
                  isKnm: true,
                })}
                color="black"
              >
                <TextInputField
                  name={organizationNameFieldName}
                  disabled={!isFieldEnabled}
                  id={organizationNameBlockId}
                  parse={normalizeFio}
                />
              </FieldBlock>
            </Row>
          )}
          {isOFFICIAL && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Должность',
                  field: `${positionFieldName}`,
                  isKnm: true,
                })}
                color="black"
              >
                <TextInputField
                  name={positionFieldName}
                  disabled={!isFieldEnabled}
                  id={positionBlockId}
                  parse={normalizeFio}
                />
              </FieldBlock>
            </Row>
          )}
          {(isUR || isFIUR) && (
            <Row>
              <FieldBlock label="Адрес места нахождения" color="black">
                <TextInputField name={locationAddressFieldName} disabled={true} />
                {!smevRequestStatus &&
                  isFnsAddressFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {isUR && (
            <Row>
              <FieldBlock label="Место нахождения (осуществления деятельности) контролируемого лица" color="black">
                <TextInputField name={workAddressFieldName} disabled={true} />
                {!smevRequestStatus &&
                  isWorkAddressFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {isIP && (
            <Row>
              <FieldBlock label="Место фактического осуществления деятельности" color="black">
                <TextInputField name={factualAddressFieldName} disabled={true} />
                {!smevRequestStatus &&
                  isWorkAddressFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {isIFIZ && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Сведения о документе, удостоверяющем личность',
                  field: `${documentFieldName}`,
                  isKnm: true,
                })}
                color="black"
                id={`${id}.document`}
              >
                <TextInputField name={documentFieldName} parse={parsePassport} disabled={!isFieldEnabled} />
              </FieldBlock>
            </Row>
          )}
          {firstOrganizationName && (
            <Row>
              <FieldBlock label="Первоначальное наименование: " button={firstOrganizationName} />
            </Row>
          )}
          {isIUR && (
            <Row>
              <FieldBlock label="Наименование" id={organizationNameBlockId}>
                <TextInputField
                  name={organizationNameFieldName}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                  disabled={!isFieldEnabled}
                />
              </FieldBlock>
            </Row>
          )}
          {(isIUR || isIFIZ) && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: isIUR
                    ? 'Государство, в котором указанное юридическое лицо зарегистрировано'
                    : 'Гражданство (при наличии)',
                  field: `${countryFieldName}`,
                  isKnm: true,
                })}
                color="black"
              >
                <SelectInputFieldAutoOptions
                  id={`${id}.country`}
                  name={countryFieldName}
                  isClearable={false}
                  size="medium"
                  optionName={CommonCatalogsNamesEnum.COUNTRIES}
                  disabled={!isFieldEnabled}
                  transformFunc={isIFIZ ? filterFlCountries : filterUlCountries}
                />
              </FieldBlock>
            </Row>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmResultItemSubjectInfoStoreProps => ({
  formRef: getKnmFormRef(state),
  isChangePlanReasonActive: getKnmIsChangePlanReasonActive(state) || false,
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isNewKnm: getKnmIsNew(state) || false,
  isSupervisor: getUserRoleIsSupervisor(state),
  knmDataApprovedType: getKnmDataErknmApproveApproved(state),
  knmDataNote: getKnmDataNote(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  subjectTypeOptions: getTargetEntitySelectOptions(state),
  catalogKnmResponsibleTypesSelectOptions: getCatalogKnmResponsibleTypesSelectOptions(state),
});

export const ErknmResultItemSubjectInfo = connect(mapStateToProps)(ErknmResultItemSubjectInfoWithoutStore);
